/**
 * Connector detail page component.
 * View connector details, capabilities, and instances.
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus as AddIcon, CheckCircle as CheckCircleIcon } from 'lucide-react';
import { Button, Chip, ShellHeader } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { connectorService } from '../services/connectorService';
import { useNotificationStore } from '../store/notificationStore';
import { CapabilityList } from '../components/connectors/CapabilityList';
import type { ConnectorDetailDto, ConnectorInstanceDto } from '../types/connector';

/**
 * Connector detail page component.
 */
export const ConnectorDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [connector, setConnector] = useState<ConnectorDetailDto | null>(null);
  const [instances, setInstances] = useState<ConnectorInstanceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  const loadConnector = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await connectorService.getConnector(id);
      setConnector(data);
      
      // Load instances
      const instanceData = await connectorService.getConnectorInstances(id);
      setInstances(instanceData);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load connector';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnector();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCreateInstance = () => {
    if (id) {
      navigate(`/connectors/${id}/instances/new`);
    }
  };

  if (loading) {
    return (
      <>
        <ShellHeader title="Connector" description="Loading…" data-testid="connector-header" />
        <PageContainer data-testid="connector-form-page">
          <LoadingSkeleton rows={6} data-testid="connector-loading" />
        </PageContainer>
      </>
    );
  }

  if (error && !connector) {
    return (
      <>
        <ShellHeader
          title="Connector"
          description="Connector not found."
          data-testid="connector-header"
          actions={
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/connectors')} data-testid="back-connector-btn">
              Back to Connectors
            </Button>
          }
        />
        <PageContainer data-testid="connector-form-page">
          <InlineErrorPanel
            message={error}
            data-testid="connector-error-panel"
            action={
              <Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>
                Dismiss
              </Button>
            }
          />
        </PageContainer>
      </>
    );
  }

  const tabs = connector ? [
    { label: 'Overview', value: 'overview' },
    { label: 'Capabilities', value: 'capabilities' },
    { label: 'Instances', value: 'instances' },
    { label: 'Usage & Analytics', value: 'usage' },
  ] : [];

  return (
    <>
      <ShellHeader
        title={connector?.name || 'Connector'}
        description={connector?.vendor ? `by ${connector.vendor}` : undefined}
        data-testid="connector-header"
        actions={
          connector ? (
            <div className="flex items-center gap-1">
              {connector.category && <Chip variant="outlined" label={connector.category} />}
              <Chip variant="outlined" label={`v${connector.version}`} />
              <Chip
                color={connector.isActive ? 'green' : 'default'}
                label={connector.isActive ? 'Active' : 'Inactive'}
              />
              <Button type="button" variant="default" size="sm" className="gap-2" onClick={handleCreateInstance} data-testid="connector-create-instance-btn">
                <AddIcon size={16} />
                Create Instance
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/connectors')} data-testid="back-connector-btn">
                Back
              </Button>
            </div>
          ) : undefined
        }
      />
      <PageContainer data-testid="connector-form-page">
        {error && (
          <InlineErrorPanel
            message={error}
            data-testid="connector-error-panel"
            action={
              <Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>
                Dismiss
              </Button>
            }
          />
        )}

        {connector && (
          <>
            <div className="page-detail-layout__tabs" role="tablist" aria-label="Connector sections">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.value}
                  className="page-detail-layout__tab"
                  data-testid={`connector-tab-${tab.value}`}
                  onClick={() => setActiveTab(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'overview' && (
              <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-panel-overview">
                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
                  <div className="flex flex-col gap-4">
                    <FormSection title="Description" data-testid="connector-section-description">
                      <p className="text-[var(--color-text-secondary)]">
                        {connector.description || 'No description available.'}
                      </p>
                    </FormSection>

                    <FormSection title="Key Features" data-testid="connector-section-key-features">
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <CheckCircleIcon className="text-green-500" />
                          <div>
                            <span className="text-sm font-medium">Authentication</span>
                            <p className="text-sm text-[var(--color-text-secondary)]">{connector.authenticationType || 'None'}</p>
                          </div>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircleIcon className="text-green-500" />
                          <div>
                            <span className="text-sm font-medium">Capabilities</span>
                            <p className="text-sm text-[var(--color-text-secondary)]">{`${connector.capabilities.length} total (${connector.capabilities.filter((c) => c.type === 'Action').length} actions, ${connector.capabilities.filter((c) => c.type === 'Trigger').length} triggers)`}</p>
                          </div>
                        </li>
                        {connector.baseUrl && (
                          <li className="flex items-center gap-2">
                            <CheckCircleIcon className="text-green-500" />
                            <div>
                              <span className="text-sm font-medium">Base URL</span>
                              <p className="text-sm text-[var(--color-text-secondary)]">{connector.baseUrl}</p>
                            </div>
                          </li>
                        )}
                        {connector.apiVersion && (
                          <li className="flex items-center gap-2">
                            <CheckCircleIcon className="text-green-500" />
                            <div>
                              <span className="text-sm font-medium">API Version</span>
                              <p className="text-sm text-[var(--color-text-secondary)]">{connector.apiVersion}</p>
                            </div>
                          </li>
                        )}
                      </ul>
                    </FormSection>
                  </div>
                  <div>
                    <FormSection title="Quick Stats" data-testid="connector-section-quick-stats">
                      <div className="flex flex-col gap-2">
                        <div>
                          <span className="text-xs text-[var(--color-text-secondary)]">
                            Capabilities
                          </span>
                          <p className="text-xl font-semibold">
                            {connector.capabilityCount}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-[var(--color-text-secondary)]">
                            Instances
                          </span>
                          <p className="text-xl font-semibold">
                            {connector.instanceCount}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-[var(--color-text-secondary)]">
                            Status
                          </span>
                          <div className="mt-0.5">
                            <Chip
                              color={connector.isActive ? 'green' : 'default'}
                              label={connector.isActive ? 'Active' : 'Inactive'}
                            />
                          </div>
                        </div>
                      </div>
                    </FormSection>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'capabilities' && (
              <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-panel-capabilities">
                <CapabilityList
                  capabilities={connector.capabilities}
                  enabledCapabilityIds={connector.capabilities.filter((c) => c.isActive).map((c) => c.id)}
                  readOnly
                  showDetails
                />
              </div>
            )}

            {activeTab === 'instances' && (
              <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-panel-instances">
                {instances.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-[var(--color-text-secondary)] mb-2">
                      No instances created yet.
                    </p>
                    <Button type="button" variant="default" size="sm" className="gap-2" onClick={handleCreateInstance}>
                      <AddIcon size={16} />
                      Create First Instance
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {instances.map((instance) => (
                      <FormSection key={instance.id} title={instance.name}>
                        <div
                          className="cursor-pointer"
                          onClick={() => navigate(`/connectors/instances/${instance.id}`)}
                        >
                          {instance.description && (
                            <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                              {instance.description}
                            </p>
                          )}
                          <div className="flex gap-1 flex-wrap">
                            <Chip
                              color={instance.isActive ? 'green' : 'default'}
                              label={instance.isActive ? 'Active' : 'Inactive'}
                            />
                          </div>
                        </div>
                      </FormSection>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'usage' && (
              <div role="tabpanel" className="page-detail-layout__panel flex flex-col gap-4" data-testid="connector-panel-usage">
                {/* Stat cards row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="connector-section-connector-statistics">
                  {[
                    { label: 'Capabilities', value: connector.capabilityCount },
                    { label: 'Active Instances', value: `${instances.filter((i) => i.isActive).length} / ${instances.length}` },
                    { label: 'Actions', value: connector.capabilities.filter((c) => c.type === 'Action').length },
                    { label: 'Triggers', value: connector.capabilities.filter((c) => c.type === 'Trigger').length },
                  ].map((stat) => (
                    <div key={stat.label} className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2">
                      <span className="text-xs text-[var(--color-text-secondary)]">{stat.label}</span>
                      <p className="text-base font-semibold mt-0.5">{stat.value}</p>
                    </div>
                  ))}
                </div>

                {/* Instance overview */}
                <FormSection title="Instance Overview" data-testid="connector-section-instance-overview">
                  {instances.length === 0 ? (
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      No instances created yet. Create an instance to start using this connector.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {instances.map((instance) => (
                        <div
                          key={instance.id}
                          className="flex items-center justify-between rounded-[0.65rem] border border-[var(--color-border-default)] bg-[var(--color-bg-inset,#fafafa)] px-3 py-2 cursor-pointer hover:bg-[var(--color-bg-subtle)] transition-colors"
                          onClick={() => navigate(`/connectors/instances/${instance.id}`)}
                        >
                          <span className="text-sm font-medium">{instance.name}</span>
                          <Chip
                            color={instance.isActive ? 'green' : 'default'}
                            label={instance.isActive ? 'Active' : 'Inactive'}
                            className="text-xs"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </FormSection>

                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                  Detailed usage analytics and trends are available for each connector instance. View an instance to see usage statistics, rate limits, and cost tracking.
                </div>
              </div>
            )}
          </>
        )}
      </PageContainer>
    </>
  );
};
